import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ServerIcon,
  DocumentTextIcon,
  BoltIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  ChartPieIcon,
  CpuChipIcon,
  CloudIcon,
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

export default function LandingPage() {
  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-md z-50 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-8 w-8 text-indigo-600" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">Kubmix Flux</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                Features
              </a>
              <Link to="/pricing" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                Pricing
              </Link>
              <a href="#demo" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                Demo
              </a>
              <Link to="/login" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                Sign In
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6">
              Modern Observability
              <br />
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Made Simple
              </span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              Full-stack observability platform. Monitor your infrastructure, applications, and logs in real-time.
              Built for modern cloud-native environments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl inline-flex items-center gap-2"
              >
                Start 14-Day Free Trial
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
              <a
                href="#demo"
                className="px-8 py-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-semibold text-lg border-2 border-gray-200 dark:border-gray-700"
              >
                Request Demo
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              No credit card required • 14-day trial • Cancel anytime
            </p>
          </div>

          {/* Hero Image/Dashboard Preview */}
          <div className="mt-16 rounded-xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-800">
            <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-1">
              <div className="bg-gray-900 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-white">
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <ChartBarIcon className="h-8 w-8 text-indigo-400 mb-2" />
                    <div className="text-2xl font-bold">99.9%</div>
                    <div className="text-sm text-gray-400">Uptime</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <BoltIcon className="h-8 w-8 text-yellow-400 mb-2" />
                    <div className="text-2xl font-bold">1.2ms</div>
                    <div className="text-sm text-gray-400">Avg Response</div>
                  </div>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <ServerIcon className="h-8 w-8 text-green-400 mb-2" />
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-sm text-gray-400">Monitoring</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Everything you need to monitor your stack
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Comprehensive observability from infrastructure to application performance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature Cards */}
            <FeatureCard
              icon={<ServerIcon className="h-12 w-12 text-indigo-600" />}
              title="Infrastructure Monitoring"
              description="Real-time visibility into your servers, containers, and cloud resources. Track CPU, memory, disk, and network metrics."
            />
            <FeatureCard
              icon={<ChartPieIcon className="h-12 w-12 text-purple-600" />}
              title="Application Performance (APM)"
              description="Distributed tracing, service maps, and performance profiling. Identify bottlenecks and optimize your code."
            />
            <FeatureCard
              icon={<DocumentTextIcon className="h-12 w-12 text-blue-600" />}
              title="Log Management"
              description="Centralized log aggregation with powerful search and filtering. Correlate logs with metrics and traces."
            />
            <FeatureCard
              icon={<CpuChipIcon className="h-12 w-12 text-green-600" />}
              title="Custom Metrics"
              description="Track business metrics alongside technical metrics. Build custom dashboards for your specific needs."
            />
            <FeatureCard
              icon={<ShieldCheckIcon className="h-12 w-12 text-red-600" />}
              title="Alerting & Incidents"
              description="Smart alerts with ML-powered anomaly detection. Integrate with PagerDuty, Slack, and more."
            />
            <FeatureCard
              icon={<CloudIcon className="h-12 w-12 text-cyan-600" />}
              title="Cloud Native"
              description="Native integrations with AWS, Azure, GCP, Kubernetes, Docker, and 500+ technologies."
            />
            <FeatureCard
              icon={<ShieldCheckIcon className="h-12 w-12 text-indigo-600" />}
              title="Cloud Security Scans"
              description="Automated security and cost analysis for your cloud infrastructure. Identify vulnerabilities and optimization opportunities."
            />
            <FeatureCard
              icon={<SparklesIcon className="h-12 w-12 text-yellow-600" />}
              title="AI-Powered Insights"
              description="Machine learning-powered anomaly detection, forecasting, and root cause analysis. Smart alerts that reduce noise."
            />
          </div>
        </div>
      </section>

      {/* Social Proof / Stats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-indigo-600 mb-2">500+</div>
              <div className="text-gray-600 dark:text-gray-400">Integrations</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-indigo-600 mb-2">1M+</div>
              <div className="text-gray-600 dark:text-gray-400">Metrics/sec</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-indigo-600 mb-2">99.9%</div>
              <div className="text-gray-600 dark:text-gray-400">SLA</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-indigo-600 mb-2">24/7</div>
              <div className="text-gray-600 dark:text-gray-400">Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Pay only for what you use
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Transparent usage-based pricing. No hidden fees. Scale as you grow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricing"
              className="px-8 py-4 bg-white text-indigo-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg inline-flex items-center justify-center gap-2"
            >
              View Pricing
              <ArrowRightIcon className="h-5 w-5" />
            </Link>
            <Link
              to="/register"
              className="px-8 py-4 bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 transition-colors font-semibold text-lg border-2 border-indigo-400"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>

      {/* Demo Request Form */}
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Request a Demo
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              See Kubmix Flux in action. Our team will walk you through the platform.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 border border-gray-200 dark:border-gray-700">
            <form className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Work Email
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Your Company"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Message (Optional)
                </label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Tell us about your monitoring needs..."
                />
              </div>
              <button
                type="submit"
                className="w-full px-6 py-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold text-lg"
              >
                Request Demo
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="#demo" className="hover:text-white transition-colors">Demo</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p>&copy; 2025 Kubmix Flux. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  );
}
